import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { io } from 'socket.io-client';
import { toast } from 'react-toastify';
import {
  Autocomplete,
  FormControlLabel,
  Switch,
  TextField,
  Tooltip,
} from '@mui/material';
import { MdCheckCircle, MdOutlineFlag, MdRefresh, MdEdit, MdCheck, MdClose, MdUndo } from 'react-icons/md';
import { Stepper, Step, StepLabel, LinearProgress } from '@mui/material';

import { Get, Post, Patch, Delete } from '../../Axios/AxiosFunctions';
import { apiHeader, apiUrl, BaseURL } from '../../config/apiUrl';
import { Button } from '../../Component/Button/Button';
import { DropDown } from '../../Component/DropDown/DropDown';
import Loader from '../../Component/Loader';
import NoData from '../../Component/NoData/NoData';
import SelectFilesModal from './SelectFilesModal';
import ConfirmRowsModal from './ConfirmRowsModal';
import classes from './ExtractionReview.module.css';

const fmtMoney = (v) =>
  v == null
    ? ''
    : Number(v).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

// the API returns message as a string OR { error: [...] }, flatten it for toasts
const apiMessage = (payload) => {
  const m = payload?.response?.data?.message ?? payload?.message;
  if (!m) return '';
  if (typeof m === 'string') return m;
  if (Array.isArray(m)) return m.join(', ');
  if (Array.isArray(m?.error)) return m.error.join(', ');
  return JSON.stringify(m);
};

/**
 * An amount the analyst can correct in place. Click the figure, type what is printed, Enter to
 * save, Esc to cancel. A corrected figure keeps the parser's reading visible beneath it ("was
 * 712,500.00") with an undo, because the most common misread -- an IRS line number fused onto
 * the value -- is exactly the kind of change someone will later want to check.
 */
const AmountCell = ({ l, disabled, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const edited = !!l.amountAt && l.parsedAmount != null;
  const start = () => {
    if (disabled) return;
    setDraft(l.amount == null ? '' : String(l.amount));
    setEditing(true);
  };
  const commit = () => {
    setEditing(false);
    const raw = draft.replace(/[$,\s]/g, '');
    if (raw === '') return;
    const n = Number(raw.replace(/^\((.*)\)$/, '-$1')); // (1,234.00) is a negative
    if (!Number.isFinite(n)) return toast.error('That is not a number');
    if (n !== l.amount) onSave(n);
  };
  if (editing)
    return (
      <input
        autoFocus
        className={classes.amountInput}
        inputMode="decimal"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={(e) => e.target.select()}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') setEditing(false);
        }}
      />
    );
  return (
    <div className={classes.amountWrap}>
      <button
        type="button"
        className={`${classes.amountBtn} ${edited ? classes.amountEdited : ''}`}
        onClick={start}
        disabled={disabled}
        title={disabled ? '' : 'Click to correct this figure'}
      >
        {l.amount == null ? '\u2014' : fmtMoney(l.amount)}
        {!disabled && <MdEdit className={classes.amountPencil} />}
      </button>
      {edited && (
        <div className={classes.amountWas}>
          was {fmtMoney(l.parsedAmount)}
          {!disabled && (
            <button type="button" className={classes.linkBtn} onClick={() => onSave(null)}>
              undo
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// A single IRS line worth more than half the return's total deductions is almost always a
// misread (the line number glued onto the figure: "712,500" for a printed 12,500). The
// workbook withholds these (financial-extraction.workbook.ts withheldLines, same 0.5 share);
// flagging them here puts them in front of the analyst while the review filter is on,
// instead of only surfacing at Confirm expense rows. Display only: it never blocks approval.
const MISREAD_SHARE = 0.5;
const looksMisread = (doc, l) => {
  if (doc?.classification?.doc_type !== 'tax_return') return false;
  const ded = Math.abs(doc?.reconciliation?.anchors?.total_deductions || 0);
  if (!ded || l.amount == null || l.ignored || l.amountAt) return false;
  if (!(l.account || l.analystAccount) || l.rolledUp || l.detailOnly || l.colOffset) return false;
  if (l.isTotal && !l.rollupOf) return false;
  if (!(l.tier === 'irs' || l.statementDetail) || l.section !== 'expenses') return false;
  return Math.abs(l.amount) / ded > MISREAD_SHARE;
};

const sourceBadge = (l) => {
  if (l.ignored) return { cls: 'badgeIgnored', text: 'ignored', tip: 'Not on the sheet' };
  if (l.analystAccount)
    return { cls: 'badgeDone', text: 'confirmed', tip: 'Confirmed by an analyst' };
  if (!l.tier) return null;
  const pct = l.confidence != null ? ` \u00b7 ${Math.round(l.confidence * 100)}%` : '';
  const map = {
    irs: ['badgeIrs', 'IRS form', 'Matched to a standard IRS form line'],
    exact: ['badgeExact', 'exact', 'Label matches the chart of accounts exactly'],
    synonym: ['badgeExact', 'learned', 'Matched from an earlier analyst decision'],
    llm: ['badgeAi', 'AI', 'Suggested by the AI mapper'],
    none: ['badgeNone', 'unmapped', 'No account found'],
  };
  const [cls, text, tip] = map[l.tier] || ['badgeNone', l.tier, ''];
  return { cls, text, tip: tip + pct };
};

const ExtractionReview = ({ listingId }) => {
  const socket = useRef(null);
  const {
    user,
    access_token: accessToken,
    company_id: companyId,
  } = useSelector(
    (state) => state.authReducer,
  );

  const [loading, setLoading] = useState(false);
  const [runs, setRuns] = useState([]);
  const [run, setRun] = useState(null); // full run (with line items)
  const [accounts, setAccounts] = useState([]);
  const [docIndex, setDocIndex] = useState(0);
  const [reviewOnly, setReviewOnly] = useState(true);
  const [showPicker, setShowPicker] = useState(false);
  const [showRows, setShowRows] = useState(false);
  // manual step override: lets the analyst revisit a finished step without the page
  // dragging them back to wherever the run currently is
  const [jumped, setJumped] = useState(null);
  // two-step delete: a document is easy to remove, but not by a single stray click
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [savingLine, setSavingLine] = useState(null);
  // Lines ticked for a bulk Accept or Ignore (Rabia, 16 Oct), by line index in this document.
  const [picked, setPicked] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [formCheck, setFormCheck] = useState(null);
  const [showFormCheck, setShowFormCheck] = useState(false);

  const getRuns = async () => {
    const res = await Get(
      BaseURL(`extraction/runs/business/${listingId}`),
      accessToken,
    );
    const list = res?.data?.data || [];
    setRuns(list);
    if (list.length && !run) getRun(list[0]._id);
  };

  const getRun = async (id, quiet = false) => {
    if (!quiet) setLoading(true);
    const res = await Get(BaseURL(`extraction/run/${id}`), accessToken);
    if (!quiet) setLoading(false);
    if (res?.data?.data) {
      setRun(res.data.data);
      setDocIndex(0);
    }
  };

  const getAccounts = async () => {
    const res = await Get(BaseURL('extraction/accounts'), accessToken);
    setAccounts(res?.data?.data?.accounts || []);
  };

  const startRun = async (files) => {
    setShowPicker(false);
    if (!files?.length) return;
    setLoading(true);
    const res = await Post(
      BaseURL('extraction/run'),
      { listingId, files },
      apiHeader(accessToken),
    );
    setLoading(false);
    if (res?.data?.data) {
      toast.success(`Extraction started on ${files.length} file(s)`);
      setJumped(null);
      setRun(res.data.data);
      setRuns((prev) => [res.data.data, ...prev]);
    }
  };

  const removeDocument = async (i) => {
    const res = await Delete(
      BaseURL(`extraction/run/${run._id}/document/${i}`),
      {},
      apiHeader(accessToken),
    );
    setConfirmRemove(null);
    if (res?.data?.data) {
      const { removed, remaining, layoutCleared } = res.data.data;
      toast.success(
        `Removed ${removed}${layoutCleared ? ', confirm the expense rows again' : ''}`,
      );
      if (docIndex >= remaining) setDocIndex(Math.max(0, remaining - 1));
      getRun(run._id);
    } else {
      toast.error(apiMessage(res) || 'Could not remove that document');
    }
  };

  // Put the finished workbook back into the deal room. The destination is CHOSEN, not
  // inferred: this writes into a live client folder, and nothing establishes where a
  // valuation belongs, so guessing could file one client's numbers under another.
  const [folders, setFolders] = useState([]);
  const [saveFolder, setSaveFolder] = useState(null);
  const [savingToRoom, setSavingToRoom] = useState(false);

  // Same endpoints the document picker browses with: the deal-room root, then one level
  // of its children. One level is enough to choose a destination and keeps this from
  // becoming a second file browser.
  const loadFolders = async () => {
    const root = await Get(
      BaseURL(`data-room/tax-return-folder?listingId=${listingId}`),
      accessToken,
      companyId,
    );
    const r = root?.data?.data;
    if (!r?._id) return;
    const flat = [{ label: `${r.name} (deal room root)`, value: r._id }];
    for (const child of r.children || []) {
      if (child.isFile) continue;
      flat.push({ label: child.name, value: child._id });
    }
    setFolders(flat);
  };

  const uploadToDealRoom = async () => {
    if (!saveFolder) {
      toast.error('Choose a folder in the deal room first');
      return;
    }
    setSavingToRoom(true);
    const res = await Post(
      BaseURL(`extraction/run/${run._id}/upload-to-deal-room`),
      { folderId: saveFolder.value },
      apiHeader(accessToken),
    );
    setSavingToRoom(false);
    if (res?.data?.data) {
      toast.success(`Saved ${res.data.data.fileName} to the deal room`);
    } else {
      toast.error(apiMessage(res) || 'Could not save to the deal room');
    }
  };

  const downloadWorkbook = async () => {
    const res = await fetch(BaseURL(`extraction/run/${run._id}/workbook`), {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      toast.error(apiMessage(err) || 'Workbook download failed');
      return;
    }
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download =
      (res.headers.get('Content-Disposition') || '').match(/filename="(.+)"/)?.[1] ||
      'SDE_Analysis.xlsx';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // Typing an account the chart does not hold adds it to the chart for good. The backend
  // refuses once if the name is a near-miss of an existing account and hands back what it
  // thinks was meant; the analyst either takes that or insists. Without this the chart
  // silently accumulates "Merchant Fees" / "Merchant fee" / "Mechant Fees".
  const confirmLine = async (lineIndex, account, force = false, learn = true) => {
    if (!account) return;
    setSavingLine(lineIndex);
    const res = await Patch(
      BaseURL(
        `extraction/run/${run._id}/document/${docIndex}/line/${lineIndex}`,
      ),
      { account, force, learn },
      apiHeader(accessToken),
    );
    setSavingLine(null);
    const data = res?.data?.data;
    if (data?.needsConfirmation) {
      setDuplicatePrompt({ lineIndex, ...data });
      return;
    }
    if (data) {
      setDuplicatePrompt(null);
      setRun(data);
    }
  };

  // Accept the mapper's suggestion as-is.
  //
  // MUI's Autocomplete returns early when the option you pick equals the value already shown
  // (useAutocomplete.js: `else if (value === newValue) return`), so onChange never fires and
  // confirmLine never runs. The AI had already written "Other Income" into these fields, which
  // meant agreeing with it was the one thing the screen could not do: the line stayed in
  // review and blocked approval no matter how many times you selected the right answer.
  // Every other control here replaces the account; this is the missing verb.
  //
  // learn=false: accepting is a click-through, not a choice, so it confirms THIS line without
  // teaching the global synonym memory. Picking from the dropdown still teaches it.
  const acceptSuggestion = (lineIndex, account) =>
    confirmLine(lineIndex, account, false, false);

  // Every way of picking an account goes through here. Picking the account the mapper already
  // proposed IS accepting it: that is what an analyst reaches for, and it was the one choice
  // the dropdown silently dropped (MUI fires no onChange for the value already selected), so
  // Adam could click the highlighted suggestion all day with nothing sent to the server.
  const chooseAccount = (l, account) => {
    if (!account) return;
    if (account === l.account && l.needsReview && !l.analystAccount)
      return acceptSuggestion(l._i, account);
    return confirmLine(l._i, account);
  };

  // Place a line the parser could not. A line with no known section is never counted as
  // resolved, so on a tax return -- where whole Schedule K pages come back "unknown" -- an
  // analyst could agree with every account on the page and still not clear the queue.
  const setSection = async (lineIndex, section) => {
    if (!section) return;
    setSavingLine(lineIndex);
    const res = await Patch(
      BaseURL(
        `extraction/run/${run._id}/document/${docIndex}/line/${lineIndex}/section`,
      ),
      { section },
      apiHeader(accessToken),
    );
    setSavingLine(null);
    const data = res?.data?.data;
    if (data) setRun(data);
  };

  // Correct (or, with null, revert) a misread figure. Stored beside the parsed value.
  const setLineAmount = async (lineIndex, amount) => {
    setSavingLine(lineIndex);
    const res = await Patch(
      BaseURL(`extraction/run/${run._id}/document/${docIndex}/line/${lineIndex}/amount`),
      { amount },
      apiHeader(accessToken),
    );
    setSavingLine(null);
    const data = res?.data?.data;
    if (data) setRun(data);
  };

  // Ignore: "this line maps to no account." Previously the ONLY way to clear a queue entry
  // was to give it an account, so junk rows — page headers, stray dates, OCR noise — got
  // mapped somewhere just to unblock approval, and that guess then lived in the synonym
  // memory for every future client. Ignoring cannot move a number: a flagged line is already
  // excluded from the workbook, so this clears the approval blocker and nothing else.
  const ignoreLine = async (lineIndex, undo = false) => {
    setSavingLine(lineIndex);
    const res = await Patch(
      BaseURL(
        `extraction/run/${run._id}/document/${docIndex}/line/${lineIndex}/ignore${
          undo ? '?undo=true' : ''
        }`,
      ),
      {},
      apiHeader(accessToken),
    );
    setSavingLine(null);
    const data = res?.data?.data;
    if (data) setRun(data);
  };


  // "Refresh" only re-reads what is already stored. When the parser itself has been fixed,
  // the stored parse is the stale thing, so re-reading it can never show the correction.
  // This re-runs the document through the parser. Confirmed accounts survive: they live in
  // the synonym store and are re-applied during mapping.
  const [reparsing, setReparsing] = useState(false);
  const [duplicatePrompt, setDuplicatePrompt] = useState(null);
  const reparseDocument = async () => {
    setReparsing(true);
    const res = await Post(
      BaseURL(`extraction/run/${run._id}/document/${docIndex}/reparse`),
      {},
      apiHeader(accessToken),
    );
    setReparsing(false);
    if (res?.data?.data) {
      setRun(res.data.data);
      toast.success('Document re-read with the current parser');
    } else {
      toast.error(apiMessage(res) || 'Could not re-read this document');
    }
  };

  // Approval locks every account picker on the document. Adam asked to be able to revisit
  // a confirmed line: re-reading the document already revokes approval, so the same door
  // opens here without a second mechanism, and the reason is stated rather than implied.
  const unapproveDocument = async () => {
    setLoading(true);
    const res = await Post(
      BaseURL(`extraction/run/${run._id}/document/${docIndex}/unapprove`),
      {},
      apiHeader(accessToken),
    );
    setLoading(false);
    if (res?.data?.data) {
      setRun(res.data.data);
      toast.success('Approval withdrawn, the accounts are editable again');
    } else {
      toast.error(apiMessage(res) || 'Could not withdraw approval');
    }
  };

  // Approving a document that does not add up, with a reason (Rabia, 28 Oct). null = closed.
  const [diffReason, setDiffReason] = useState(null);
  const approveDocument = async (acceptDifference) => {
    setLoading(true);
    const res = await Post(
      BaseURL(`extraction/run/${run._id}/document/${docIndex}/approve`),
      acceptDifference ? { acceptDifference } : {},
      apiHeader(accessToken),
    );
    setLoading(false);
    if (res?.data?.data) {
      toast.success(acceptDifference ? 'Document approved with the difference noted' : 'Document approved');
      setDiffReason(null);
      setRun(res.data.data);
    } else {
      toast.error(
        apiMessage(res) ||
          'Approval refused: resolve flagged lines and reconciliation first',
      );
    }
  };

  useEffect(() => {
    getRuns();
    getAccounts();
  }, [listingId]);

  // Extraction takes minutes on a tax return. The socket pushes updates, but if it drops
  // the page would sit silently on "processing" forever and look hung, so poll while any
  // document is still in flight, and stop as soon as it settles.
  useEffect(() => {
    const working = (run?.documents || []).some((d) =>
      ['pending', 'processing'].includes(d.status),
    );
    if (!run?._id || !working) return undefined;
    const t = setInterval(() => getRun(run._id, true), 4000);
    return () => clearInterval(t);
  }, [run]);

  useEffect(() => {
    if (!socket.current) socket.current = io(apiUrl, { transports: ['websocket'] });
    if (socket.current && user?._id) {
      socket.current.emit('join', { id: user._id });
      const onUpdate = ({ run: updated }) => {
        if (String(updated?.listingId) !== String(listingId)) return;
        setRuns((prev) =>
          prev.map((r) => (r._id === updated._id ? { ...r, status: updated.status } : r)),
        );
        setRun((cur) => (cur && cur._id === updated._id ? updated : cur));
      };
      socket.current.on('extraction', onUpdate);
      return () => socket.current.off('extraction', onUpdate);
    }
  }, [user?._id, listingId]);

  const doc = run?.documents?.[docIndex];
  const recon = doc?.reconciliation;
  // One definition of "a decision the analyst must make", used by BOTH the document tabs
  // and the panel below them. They previously disagreed: the tab required an account to
  // already be set and the panel did not, so a document could advertise "nothing to review"
  // while the panel underneath listed rows blocking approval.
  //
  // Only actionable reviews count, which matches the backend approval gate. Unmapped noise
  // on tax-return worksheet pages never enters the workbook and does not block approval.
  //
  // Unreadable lines are NOT decisions. A redacted or poorly scanned page yields rows like
  // "& 21 Total deductions.(attachAdd lines statement) 7 through20 -@Oy" = 480,216,126,752:
  // a label that is mostly punctuation and a figure hundreds of times the business's own
  // revenue. Nobody can classify that, and the scale guard already keeps such figures out
  // of the workbook. Putting them in the queue only makes the real decisions harder to find.
  const scaleOf = (d) =>
    Math.max(
      Math.abs(d?.reconciliation?.anchors?.gross_receipts || 0),
      Math.abs(d?.reconciliation?.anchors?.total_income || 0),
      ...(d?.lineItems || [])
        .filter((l) => l.isTotal && l.amount != null)
        .map((l) => Math.abs(l.amount)),
      0,
    );
  const unreadableAt = (sc, l) => {
    const label = String(l.label || '');
    const legible =
      label.length > 10 &&
      [...label].filter((c) => /[A-Za-z ]/.test(c)).length / label.length < 0.72;
    const absurd = sc > 0 && Math.abs(l.amount || 0) > sc * 1.05;
    return (legible || absurd) && !l.account;
  };
  const actionableAt = (sc, l) =>
    l.needsReview &&
    !l.isTotal &&
    !l.isHeader &&
    !unreadableAt(sc, l) &&
    (l.account || l.multiColumn || (l.section && l.section !== 'unknown'));
  const actionableCount = (d) => {
    const sc = scaleOf(d);
    return (d?.lineItems || []).filter((l) => actionableAt(sc, l)).length;
  };

  // How many printed lines each category covers. A category is the ONE row an analyst
  // answers; its members are already inside its subtotal and have no row of their own on
  // the deliverable, so the picker tells them that answering the category settles the rest.
  const groupSize = useMemo(() => {
    const n = {};
    for (const l of doc?.lineItems || []) {
      if (l.groupParent && !l.groupTotalOf) n[l.groupParent] = (n[l.groupParent] || 0) + 1;
    }
    return n;
  }, [doc]);

  const scale = scaleOf(doc);
  const isUnreadable = (l) => unreadableAt(scale, l);
  const isActionable = (l) => actionableAt(scale, l);
  const reviewCount = useMemo(
    () => (doc?.lineItems || []).filter(isActionable).length,
    [doc],
  );
  const unreadableCount = useMemo(
    () => (doc?.lineItems || []).filter((l) => !l.isHeader && isUnreadable(l)).length,
    [doc],
  );
  const visibleLines = useMemo(() => {
    const lines = (doc?.lineItems || [])
      .map((l, i) => ({ ...l, _i: i }))
      .filter((l) => !l.isHeader && l.amount != null);
    return reviewOnly ? lines.filter((l) => isActionable(l) || looksMisread(doc, l)) : lines;
  }, [doc, reviewOnly]);

  // The return's page-1 lines and checks, read the way the workbook reads them.
  useEffect(() => {
    setFormCheck(null);
    if (!run?._id || doc?.classification?.doc_type !== 'tax_return') return;
    let live = true;
    Get(BaseURL(`extraction/run/${run._id}/document/${docIndex}/form-check`), accessToken, null, false).then((r) => {
      if (live) setFormCheck(r?.data?.data || null);
    });
    return () => {
      live = false;
    };
  }, [run?._id, docIndex, doc?.lineItems, doc?.classification?.doc_type, accessToken]);

  // The same tests the per-row Accept and Ignore buttons use.
  const canAcceptLine = (l) => l.needsReview && l.account && !l.analystAccount && !l.ignored;
  const canIgnoreLine = (l) => l.needsReview && !l.analystAccount && !l.isTotal && !l.rolledUp && !l.ignored;
  const pickable = visibleLines.filter((l) => canAcceptLine(l) || canIgnoreLine(l));
  const pickedLines = pickable.filter((l) => picked.has(l._i));
  const pickedAccept = pickedLines.filter(canAcceptLine);
  const pickedIgnore = pickedLines.filter(canIgnoreLine);
  // A new document, a re-read or a changed filter starts the selection over.
  useEffect(() => setPicked(new Set()), [docIndex, run?._id, reviewOnly]);
  const togglePick = (i) =>
    setPicked((p) => {
      const n = new Set(p);
      n.has(i) ? n.delete(i) : n.add(i);
      return n;
    });
  const bulk = async (action, lines) => {
    if (!lines.length) return;
    setBulkBusy(true);
    const res = await Patch(
      BaseURL(`extraction/run/${run._id}/document/${docIndex}/lines`),
      { action, lineIndexes: lines.map((l) => l._i) },
      apiHeader(accessToken),
    );
    setBulkBusy(false);
    const data = res?.data?.data;
    if (!data) return;
    setRun(data.run);
    setPicked(new Set());
    const verb = action === 'accept' ? 'Accepted' : 'Ignored';
    if (data.done.length) toast.success(`${verb} ${data.done.length} line${data.done.length === 1 ? '' : 's'}`);
    if (data.skipped.length)
      toast.info(`${data.skipped.length} skipped: ${data.skipped.slice(0, 3).map((x) => x.reason).join('; ')}${data.skipped.length > 3 ? '; …' : ''}`);
  };

  // Where the work has actually got to, from run state alone. The analyst can click back to
  // any completed step; `jumped` holds that override so the page does not yank them forward.
  const docs = run?.documents || [];
  const busy = docs.some((d) => ['pending', 'processing'].includes(d.status));
  const rowsConfirmed = !!run?.expenseLayout?.rows?.length;
  const approvedCount = docs.filter((d) => d.status === 'approved').length;
  // Review the extracted lines and approve the documents BEFORE deciding the sheet layout:
  // the rows are derived from approved data, so confirming them first means confirming a
  // layout built on figures nobody has checked.
  const allApproved = docs.length > 0 && approvedCount === docs.length;
  const derivedStep = !run
    ? 0
    : busy
    ? 1
    : !allApproved
    ? 2
    : !rowsConfirmed
    ? 3
    : 4;
  const step = jumped != null ? jumped : derivedStep;
  const furthest = Math.max(derivedStep, jumped ?? 0);

  const STEPS = [
    { label: 'Select documents', hint: 'Choose the returns and P&Ls for this valuation' },
    { label: 'Extract & reconcile', hint: 'Each document is checked against its own totals' },
    { label: 'Review & approve', hint: 'Resolve flagged lines, then approve each document' },
    { label: 'Confirm expense rows', hint: 'Approve the rows that will appear on the sheet' },
    { label: 'Download workbook', hint: 'The finished SDE analysis' },
  ];

  return (
    <div className={classes.container}>
      <div className={classes.toolbarSlim}>
        <DropDown
          // DropDown reads option.name unless optionLabel says otherwise, so a {label,
          // value} list renders as blank rows without these two props.
          optionLabel="label"
          optionValue="value"
          options={runs.map((r) => ({
            label: `${new Date(r.createdAt).toLocaleString()}, ${r.status}`,
            value: r._id,
          }))}
          value={
            run
              ? {
                  label: `${new Date(run.createdAt).toLocaleString()}, ${run.status}`,
                  value: run._id,
                }
              : null
          }
          setter={(v) => v?.value && getRun(v.value)}
          placeholder="Select extraction run"
        />
        <Button label="Check for updates" onClick={() => run && getRun(run._id)}>
          <MdRefresh />
        </Button>
      </div>

      <div className={classes.stepper}>
        <Stepper activeStep={step} alternativeLabel nonLinear>
          {STEPS.map((st, i) => (
            <Step key={st.label} completed={i < furthest}>
              <StepLabel
                onClick={() => i <= furthest && setJumped(i)}
                style={{ cursor: i <= furthest ? 'pointer' : 'default' }}
              >
                {st.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </div>

      {/* One clear instruction and one primary action for wherever the analyst is. */}
      <div className={classes.guide}>
        <div>
          <div className={classes.guideStep}>
            Step {step + 1} of {STEPS.length}
          </div>
          <div className={classes.guideTitle}>{STEPS[step].label}</div>
          <div className={classes.guideHint}>
            {step === 0 &&
              'Pick the tax returns and P&L statements for this deal. We suggest the last three years plus the current year, and pre-tick them for you.'}
            {step === 1 &&
              'Reading each document and checking it against its own stated totals. Tax returns take a few minutes.'}
            {step === 2 &&
              'Set an account for each highlighted line, then approve. You only answer categories: a line marked "part of" is already inside a category subtotal and gets no row of its own, exactly as on a finished valuation. Approval is blocked until the document reconciles.'}
            {step === 3 &&
              'These rows become the workbook. Rename, merge or remove any of them, and answer anything we could not classify.'}
            {step === 4 &&
              'Every document is approved. Download the SDE analysis, seven tabs, all formulas live.'}
          </div>
        </div>
        <div className={classes.guideActions}>
          {step === 0 && (
            <Button label="Choose documents" onClick={() => setShowPicker(true)} />
          )}
          {step === 1 && (
            <Button label="Check for updates" onClick={() => run && getRun(run._id)}>
              <MdRefresh />
            </Button>
          )}
          {step === 3 && (
            <Button label="Review expense rows" onClick={() => setShowRows(true)} />
          )}
          {step >= 4 && (
            <>
              <Button
                label="Review expense rows"
                onClick={() => setShowRows(true)}
              />
              <div className={classes.saveToRoom}>
                <DropDown
                  options={folders}
                  value={saveFolder}
                  setter={setSaveFolder}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Save to deal room folder"
                />
                <Button
                  label={savingToRoom ? 'Saving...' : 'Save to deal room'}
                  disabled={!approvedCount || !saveFolder || savingToRoom}
                  title={
                    approvedCount
                      ? 'Upload the workbook into the deal room'
                      : 'Approve at least one document first'
                  }
                  onClick={uploadToDealRoom}
                />
              </div>
              <Button
                label="Download workbook"
                disabled={!approvedCount}
                title={
                  approvedCount
                    ? 'Combined SDE workbook from approved documents'
                    : 'Approve at least one document first'
                }
                onClick={downloadWorkbook}
              />
            </>
          )}
          {step > 0 && (
            <Button label="Start another run" onClick={() => setShowPicker(true)} />
          )}
        </div>
      </div>

      {!loading && run && busy && (
        <div className={classes.progressBox}>
          {(() => {
            const done = docs.filter(
              (d) => !['pending', 'processing'].includes(d.status),
            ).length;
            const current = docs.find((d) => d.status === 'processing');
            return (
              <>
                <div className={classes.progressHead}>
                  <b>
                    Reading document {Math.min(done + 1, docs.length)} of {docs.length}
                  </b>
                  <span className={classes.progressPct}>
                    {Math.round((done / Math.max(docs.length, 1)) * 100)}%
                  </span>
                </div>
                <LinearProgress
                  variant="determinate"
                  value={(done / Math.max(docs.length, 1)) * 100}
                />
                <div className={classes.progressNow}>
                  {current ? current.fileName : 'starting…'}
                </div>
                <div className={classes.progressHint}>
                  Each document is read, its accounts matched, and its figures checked against
                  its own totals. A tax return can take a few minutes. You can leave this page
                  and come back.
                </div>
              </>
            );
          })()}
        </div>
      )}

      {loading && <Loader />}
      {!loading && !run && (
        <NoData text="No extraction runs yet, choose documents above to start one." />
      )}

      {/* Document cards are context for the steps that act on documents. On step 1 they
          show extraction progress; on step 4 they select which document the table below
          belongs to. They are noise while choosing files or downloading. */}
      {!loading && run && step >= 1 && step <= 3 && (
        <>
          <div className={classes.docTabs}>
            {run.documents.map((d, i) => {
              const flagged = actionableCount(d);
              const reconOk = d.reconciliation?.status === 'reconciled';
              const pill =
                d.status === 'approved'
                  ? classes.pillOk
                  : d.status === 'error'
                  ? classes.pillBad
                  : reconOk
                  ? classes.pillMuted
                  : classes.pillWarn;
              return (
                <div
                  key={i}
                  className={`${classes.docTab} ${
                    i === docIndex ? classes.docTabActive : ''
                  }`}
                  onClick={() => setDocIndex(i)}
                  title={d.fileName}
                >
                  <div className={classes.docTabName}>{d.fileName}</div>
                  <div className={classes.docTabMeta}>
                    <span className={`${classes.pill} ${pill}`}>
                      {['pending', 'processing'].includes(d.status)
                        ? d.status === 'processing'
                          ? 'reading…'
                          : 'queued'
                        : d.status === 'approved'
                        ? 'approved'
                        : d.status === 'error'
                        ? 'failed'
                        : reconOk
                        ? 'checks out'
                        : "doesn't add up"}
                    </span>
                    <span className={classes.docTabCount}>
                      {['pending', 'processing'].includes(d.status)
                        ? ''
                        : flagged
                        ? `${flagged} to review`
                        : 'nothing to review'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {doc && step === 2 && (
            <>
              <div className={classes.docActions}>
                <div className={classes.docActionsName}>{doc.fileName}</div>
                {confirmRemove === docIndex ? (
                  <>
                    <span className={classes.docConfirmAsk}>
                      Remove this document from the run?
                    </span>
                    <Button
                      label="Yes, remove"
                      onClick={() => removeDocument(docIndex)}
                    />
                    <Button label="Keep it" onClick={() => setConfirmRemove(null)} />
                  </>
                ) : (
                  <Button
                    label="Remove from run"
                    title="Take this document out of the run. It stays in the deal room"
                    onClick={() => setConfirmRemove(docIndex)}
                  />
                )}
              </div>

              <div className={classes.statRow}>
                <div className={classes.stat}>
                  <div className={classes.statLabel}>Document</div>
                  <div className={classes.statValue}>
                    {doc.classification?.doc_type === 'tax_return'
                      ? 'Tax return'
                      : doc.classification?.doc_type === 'profit_loss'
                      ? 'P&L'
                      : doc.classification?.doc_type || 'not read'}
                  </div>
                  {doc.classification?.form && (
                    <div className={classes.statSub}>{doc.classification.form}</div>
                  )}
                </div>
                <div className={classes.stat}>
                  <div className={classes.statLabel}>Period</div>
                  <div className={classes.statValue}>{doc.classification?.year || 'not read'}</div>
                  {doc.classification?.period?.label && (
                    <div className={classes.statSub}>
                      {doc.classification.period.label}
                    </div>
                  )}
                </div>
                <div className={classes.stat}>
                  <div className={classes.statLabel}>Accounts mapped</div>
                  <div className={classes.statValue}>
                    {doc.mapping?.mapped ?? 0}
                    <span className={classes.statSub}>
                      {' '}/ {doc.mapping?.total_detail_lines ?? 0}
                    </span>
                  </div>
                  <div className={classes.statSub}>detail lines offered to the mapper</div>
                </div>
                <div className={classes.stat}>
                  <div className={classes.statLabel}>Set aside</div>
                  <div className={classes.statValue}>{unreadableCount}</div>
                  <div className={classes.statSub}>
                    {unreadableCount
                      ? 'unreadable on the scan, so not your call'
                      : 'nothing unreadable'}
                  </div>
                </div>
                <div className={classes.stat}>
                  <div className={classes.statLabel}>Needs review</div>
                  <div
                    className={`${classes.statValue} ${
                      reviewCount ? classes.statFlag : classes.statOk
                    }`}
                  >
                    {reviewCount}
                  </div>
                  <div className={classes.statSub}>
                    {reviewCount ? 'blocking approval' : 'nothing blocking'}
                  </div>
                </div>
              </div>

              {recon && (
                <div
                  className={
                    recon.status === 'reconciled'
                      ? classes.reconOk
                      : classes.reconBad
                  }
                >
                  {recon.status === 'reconciled' ? (
                    <>
                      <MdCheckCircle /> Checks out. The detail we extracted matches this
                      statement's own totals
                      {(() => {
                        const n = (recon.checks || []).filter((c) => c.pass !== null).length;
                        // A document proved by one identity is far weaker evidence than one
                        // proved by three; saying only "reconciled" hides that difference.
                        return (
                          <div className={classes.reconDetail}>
                            Verified by {n} independent check{n === 1 ? '' : 's'}
                            {n <= 1
                              ? ' . Only one could be tested on this document, so treat it with more caution.'
                              : '.'}
                          </div>
                        );
                      })()}
                    </>
                  ) : (
                    <>
                      <MdOutlineFlag /> This document does not add up:{' '}
                      {(recon.failed || []).join(', ') || 'no checks evaluable'}
                      {(recon.checks || [])
                        .filter((c) => c.pass === false)
                        .map((c) => (
                          <div key={c.name} className={classes.reconDetail}>
                            The statement states {fmtMoney(c.stated)} but the lines we read
                            add up to {fmtMoney(c.computed)}, a difference of{' '}
                            {fmtMoney(Math.abs(c.delta))}.
                          </div>
                        ))}
                      <div className={classes.reconFix}>
                        <b>What to do:</b> this is not something you can correct by editing
                        rows. The figures we read disagree with the statement's own totals,
                        so the reading itself is wrong. Use a different document for this
                        period: a single-period statement rather than a comparison or a
                        summary, or a cleaner copy if this one is a scan. If you have checked
                        the figures against the document and they are right (a rounding or a
                        line the statement itself left out), you can approve it with the
                        difference: give the reason, and it is kept with the document.
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* The chart grows permanently, so a near-miss gets one question before it
                  becomes a fifth way to spell the same account. */}
              {duplicatePrompt && (
                <div className={classes.dupPrompt}>
                  <span>
                    <strong>{duplicatePrompt.proposed}</strong> looks like{' '}
                    <strong>{duplicatePrompt.suggestion}</strong>, which already exists (
                    {duplicatePrompt.why}).
                  </span>
                  <button
                    type="button"
                    className={classes.dupPrimary}
                    onClick={() => {
                      const { lineIndex, suggestion } = duplicatePrompt;
                      setDuplicatePrompt(null);
                      confirmLine(lineIndex, suggestion);
                    }}
                  >
                    Use {duplicatePrompt.suggestion}
                  </button>
                  <button
                    type="button"
                    className={classes.dupLink}
                    onClick={() => {
                      const { lineIndex, proposed } = duplicatePrompt;
                      setDuplicatePrompt(null);
                      confirmLine(lineIndex, proposed, true);
                    }}
                  >
                    add mine anyway
                  </button>
                  <button
                    type="button"
                    className={classes.dupClose}
                    onClick={() => setDuplicatePrompt(null)}
                    aria-label="Dismiss"
                  >
                    x
                  </button>
                </div>
              )}

              {doc.status === 'error' && (
                <div className={classes.docError}>
                  <strong>This document could not be read.</strong>{' '}
                  {doc.message ||
                    'No reason was recorded. Try "Re-read document" above.'}
                </div>
              )}

              {(doc.warnings?.length > 0 ||
                ['incomplete', 'partial'].includes(doc.completeness?.status)) && (
                <div className={classes.warnBanner}>
                  <MdOutlineFlag />
                  <div>
                    <b>
                      {doc.completeness?.status === 'incomplete'
                        ? 'This document is missing pages it refers to'
                        : doc.completeness?.status === 'partial'
                        ? 'Some referenced detail is not in this document'
                        : 'Check this document'}
                    </b>
                    {(doc.warnings || []).map((w, i) => (
                      <div key={i} className={classes.warnLine}>
                        {w}
                      </div>
                    ))}
                    {(doc.completeness?.findings || [])
                      .filter((f) => f.shortfall)
                      .map((f, i) => (
                        <div key={`f${i}`} className={classes.warnLine}>
                          {f.line}: {fmtMoney(f.shortfall)} of detail not found in this file.
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {formCheck && (
                <div className={`${classes.formCheck} ${formCheck.ok ? classes.formCheckOk : classes.formCheckWarn}`}>
                  <button type="button" className={classes.formCheckHead} onClick={() => setShowFormCheck((x) => !x)} aria-expanded={showFormCheck}>
                    {formCheck.ok ? <MdCheckCircle /> : <MdOutlineFlag />}
                    <b>Return check, Form {formCheck.form} (page 1 is page {formCheck.page} of the file)</b>
                    <span>
                      {formCheck.ok
                        ? 'The page 1 lines balance, so the workbook takes each figure from its own line.'
                        : 'The page 1 lines do not balance yet, so the workbook reads this return page by page.'}
                    </span>
                    <span className={classes.formCheckToggle}>{showFormCheck ? 'Hide' : 'Show lines'}</span>
                  </button>
                  {showFormCheck && (
                    <div className={classes.formCheckBody}>
                      {formCheck.checks.map((c) => (
                        <div key={c.name} className={classes.warnLine}>
                          {c.ok ? 'OK' : 'Off'}: {c.name}. Return says {fmtMoney(c.stated)}, lines give {fmtMoney(c.computed)}
                          {c.ok ? '' : ` (${fmtMoney(c.computed - c.stated)})`}.
                        </div>
                      ))}
                      <table className={classes.formCheckTable}>
                        <thead>
                          <tr>
                            <th>Line</th>
                            <th>Item</th>
                            <th className={classes.thNum}>Amount</th>
                            <th>Page</th>
                            <th>Note</th>
                          </tr>
                        </thead>
                        <tbody>
                          {formCheck.lines.map((x) => (
                            <tr key={x.line}>
                              <td>{x.line}</td>
                              <td>{x.item}</td>
                              <td className={classes.num}>{x.value == null ? '' : fmtMoney(x.value)}</td>
                              <td>{x.value == null ? '' : x.page}</td>
                              <td>{x.note || ''}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className={classes.warnLine}>
                        {formCheck.otherDeductions
                          ? `Line 20 is itemised from "${formCheck.otherDeductions.title}" on page ${formCheck.otherDeductions.page} (${formCheck.otherDeductions.rows} rows). Anything it does not name goes to "Other expenses (not itemised on the return)".`
                          : 'No line 20 statement was found, so line 20 goes to "Other expenses (not itemised on the return)".'}
                        {formCheck.comparisonPages.length > 0 &&
                          ` Comparison worksheet on page ${formCheck.comparisonPages.join(', ')} is not read as this year's figures.`}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className={classes.tableControls}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={reviewOnly}
                      onChange={(e) => setReviewOnly(e.target.checked)}
                    />
                  }
                  label="Needs review only"
                />
                {pickedLines.length > 0 && doc.status !== 'approved' && (
                  <div className={classes.bulkBar}>
                    <b>{pickedLines.length} selected</b>
                    <button
                      type="button"
                      className={`${classes.actBtn} ${classes.actAccept}`}
                      disabled={bulkBusy || !pickedAccept.length}
                      title="Accept each selected line's suggested account (does not teach it to future documents)"
                      onClick={() => bulk('accept', pickedAccept)}
                    >
                      <MdCheck /> Accept {pickedAccept.length}
                    </button>
                    <button
                      type="button"
                      className={`${classes.actBtn} ${classes.actIgnore}`}
                      disabled={bulkBusy || !pickedIgnore.length}
                      title="Drop the selected lines from the review queue; no total changes"
                      onClick={() => bulk('ignore', pickedIgnore)}
                    >
                      <MdClose /> Ignore {pickedIgnore.length}
                    </button>
                    <button type="button" className={classes.actBtn} disabled={bulkBusy} onClick={() => setPicked(new Set())}>
                      Clear
                    </button>
                    {bulkBusy && <span>Saving…</span>}
                  </div>
                )}
                <Button
                  label={reparsing ? 'Re-reading...' : 'Re-read document'}
                  disabled={reparsing || ['pending', 'processing'].includes(doc.status)}
                  title="Read this file again with the current parser. Accounts you have already confirmed are kept."
                  onClick={reparseDocument}
                >
                  <MdRefresh />
                </Button>
                {doc.status === 'approved' ? (
                  <Button
                    label="Edit again"
                    title="Withdraw approval so the accounts can be changed"
                    onClick={unapproveDocument}
                  />
                ) : (
                  <>
                    {recon?.status !== 'reconciled' && reviewCount === 0 && (
                      <Button
                        label="Approve with the difference…"
                        title="Approve although the lines do not add up to the statement's totals, with your reason"
                        onClick={() => setDiffReason(diffReason === null ? '' : null)}
                      />
                    )}
                    <Button
                      label="Approve Document"
                      disabled={reviewCount > 0 || recon?.status !== 'reconciled'}
                      onClick={() => approveDocument()}
                    />
                  </>
                )}
              </div>

              {diffReason !== null && doc.status !== 'approved' && (
                <div className={classes.reconFix} style={{ margin: '0 0 12px' }}>
                  <b>Approve with the difference.</b> Say why the figures are right even though they do not add up
                  to the statement's totals. Your reason and the difference are kept with the document.
                  <textarea
                    rows={3}
                    style={{ display: 'block', width: '100%', marginTop: 8, padding: 8, borderRadius: 6, border: '1px solid #cfd5de', font: 'inherit' }}
                    placeholder="e.g. Checked every line against the PDF; the statement's total leaves out the $1,121.89 credit card fees line."
                    value={diffReason}
                    onChange={(e) => setDiffReason(e.target.value)}
                  />
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <Button label="Approve with this reason" disabled={diffReason.trim().length < 10} onClick={() => approveDocument(diffReason.trim())} />
                    <Button label="Cancel" onClick={() => setDiffReason(null)} />
                  </div>
                </div>
              )}
              {doc.approvedWithDifference?.reason && (
                <div className={classes.reconFix} style={{ margin: '0 0 12px' }}>
                  <b>Approved with a difference:</b> {doc.approvedWithDifference.reason}
                </div>
              )}

              <div className={classes.tableWrap}>
                <table className={classes.table}>
                  <thead>
                    <tr>
                      <th className={classes.thPick}>
                        {doc.status !== 'approved' && pickable.length > 0 && (
                          <input
                            type="checkbox"
                            aria-label="Select every line that can be accepted or ignored"
                            title="Select every line shown that can be accepted or ignored"
                            checked={pickedLines.length === pickable.length}
                            ref={(el) => el && (el.indeterminate = pickedLines.length > 0 && pickedLines.length < pickable.length)}
                            onChange={(e) => setPicked(new Set(e.target.checked ? pickable.map((l) => l._i) : []))}
                          />
                        )}
                      </th>
                      <th>Line (as printed)</th>
                      <th className={classes.thNum}>Amount</th>
                      <th>Section</th>
                      <th>Account</th>
                      <th>Source</th>
                      <th className={classes.thActions} />
                    </tr>
                  </thead>
                  <tbody>
                    {visibleLines.map((l) => {
                      const locked = savingLine === l._i || doc.status === 'approved';
                      const canAccept = l.needsReview && l.account && !l.analystAccount && !l.ignored;
                      const canIgnore =
                        l.needsReview && !l.analystAccount && !l.isTotal && !l.rolledUp && !l.ignored;
                      const badge = sourceBadge(l);
                      const misread = looksMisread(doc, l);
                      const pickable = !(l.rolledUp || l.detailOnly || l.isTotal);
                      return (
                        <tr
                          key={l._i}
                          className={[
                            classes.row,
                            l.ignored ? classes.rowIgnored : '',
                            (l.needsReview || misread) && !l.ignored ? classes.rowFlag : '',
                            savingLine === l._i ? classes.rowSaving : '',
                          ].join(' ')}
                        >
                          {/* Where this figure came from: the table necessarily shows a cleaned,
                              truncated label, so the full line as printed, its page and how it was
                              matched are one hover away. */}
                          <td className={classes.pickCell}>
                            {doc.status !== 'approved' && (canAccept || canIgnore) && (
                              <input
                                type="checkbox"
                                aria-label={`Select ${l.label || 'line'}`}
                                checked={picked.has(l._i)}
                                disabled={bulkBusy}
                                onChange={() => togglePick(l._i)}
                              />
                            )}
                          </td>
                          <td className={classes.lineCell}>
                            <div
                              className={classes.lineLabel}
                              title={[
                                `As printed: ${l.label ?? ''}`,
                                l.parsedAmount != null
                                  ? `Read as ${fmtMoney(l.parsedAmount)}, corrected to ${fmtMoney(l.amount)}`
                                  : null,
                                l.page ? `Page ${l.page} of ${doc.fileName}` : null,
                                l.rolledUp
                                  ? `Counted inside ${l.rolledUp}`
                                  : l.groupParent
                                  ? `Under ${l.groupParent}`
                                  : null,
                              ]
                                .filter(Boolean)
                                .join('\n')}
                            >
                              {l.acctNo ? <span className={classes.acctNo}>{l.acctNo}</span> : null}
                              {l.label}
                              {l.isTotal ? <span className={classes.totalTag}>total</span> : null}
                            </div>
                            <div className={classes.lineMeta}>
                              {[
                                l.page ? `p.${l.page}` : null,
                                l.rolledUp
                                  ? `inside ${l.rolledUp}`
                                  : l.groupParent
                                  ? `under ${l.groupParent}`
                                  : null,
                              ]
                                .filter(Boolean)
                                .join(' \u00b7 ')}
                            </div>
                          </td>

                          <td className={classes.amount}>
                            <AmountCell
                              l={l}
                              disabled={locked || l.ignored}
                              onSave={(v) => setLineAmount(l._i, v)}
                            />
                          </td>

                          {/* Section tells approval where a line sits; it does not move money --
                              the sheet places a figure by its ACCOUNT's section. Offered only
                              where it cannot create work: a line in review with no account and
                              an unknown section does not block approval, and giving it a
                              section would turn it into a blocker. */}
                          <td>
                            {!pickable ||
                            l.ignored ||
                            !(l.account || l.analystAccount || (l.section && l.section !== 'unknown')) ? (
                              <span className={classes.sectionText}>{l.section || '\u2014'}</span>
                            ) : (
                              <select
                                className={classes.sectionSelect}
                                value={
                                  ['income', 'cogs', 'expenses', 'other'].includes(l.section)
                                    ? l.section
                                    : ''
                                }
                                disabled={locked}
                                onChange={(e) => setSection(l._i, e.target.value)}
                              >
                                <option value="" disabled>
                                  {l.section || 'unknown'}
                                </option>
                                <option value="income">income</option>
                                <option value="cogs">cogs</option>
                                <option value="expenses">expenses</option>
                                <option value="other">other</option>
                              </select>
                            )}
                          </td>

                          <td className={classes.accountCell}>
                            {/* Components of a category are not decisions: the subtotal one
                                line below already carries them. Say where the money went
                                instead of offering a picker nobody can answer. */}
                            {l.rolledUp || l.detailOnly ? (
                              <span className={classes.rolledInto}>
                                {l.detailOnly
                                  ? `part of ${l.section === 'cogs' ? 'Cost of Goods Sold' : 'Sales Revenue'}`
                                  : `part of ${l.account || l.rolledUp}`}
                                , no separate row
                              </span>
                            ) : l.rollupOf ? (
                              <span className={classes.rolledInto}>
                                {l.account}
                                {l.rollupChildren?.length ? ` (${l.rollupChildren.length} lines)` : ''}
                              </span>
                            ) : l.isTotal ? (
                              <span className={classes.rolledInto}>subtotal</span>
                            ) : l.ignored ? (
                              <span className={classes.rolledInto}>not on the sheet</span>
                            ) : (
                              <Autocomplete
                                size="small"
                                freeSolo
                                options={accounts}
                                value={l.account || ''}
                                disabled={locked}
                                onChange={(e, v) => chooseAccount(l, v)}
                                // MUI swallows a click on the option that is already the value;
                                // catch that one click and treat it as accepting it.
                                renderOption={(props, option) => {
                                  const { key, ...rest } = props;
                                  return (
                                    <li
                                      key={key}
                                      {...rest}
                                      onClick={(e) => {
                                        if (option === l.account) chooseAccount(l, option);
                                        rest.onClick?.(e);
                                      }}
                                    >
                                      {option}
                                    </li>
                                  );
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    placeholder={
                                      groupSize[l.groupOpens]
                                        ? 'account for this category'
                                        : 'choose an account'
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' && e.target.value)
                                        chooseAccount(l, e.target.value);
                                    }}
                                  />
                                )}
                              />
                            )}
                            {!!groupSize[l.groupOpens] && !l.rolledUp && (
                              <div className={classes.groupHint}>
                                category, also settles {groupSize[l.groupOpens]} line
                                {groupSize[l.groupOpens] > 1 ? 's' : ''} inside it
                              </div>
                            )}
                          </td>

                          <td>
                            {misread && (
                              <Tooltip title="Larger than half of this return's total deductions, so it will not reach the sheet. Check it against the PDF and click the amount to correct it.">
                                <span className={`${classes.badge} ${classes.badgeMisread}`}>
                                  looks misread
                                </span>
                              </Tooltip>
                            )}
                            {badge && (
                              <Tooltip title={badge.tip}>
                                <span className={`${classes.badge} ${classes[badge.cls]}`}>
                                  {badge.text}
                                </span>
                              </Tooltip>
                            )}
                          </td>

                          <td className={classes.actionsCell}>
                            <div className={classes.actions}>
                              {canAccept && (
                                <Tooltip
                                  title={
                                    l.multiColumn
                                      ? `Accept "${l.account}" -- check the amount first: this line spans two form columns`
                                      : `Accept "${l.account}" for this line (does not teach it to future documents)`
                                  }
                                >
                                  <span>
                                    <button
                                      type="button"
                                      className={`${classes.actBtn} ${classes.actAccept}`}
                                      disabled={locked}
                                      onClick={() => acceptSuggestion(l._i, l.account)}
                                    >
                                      <MdCheck /> Accept
                                    </button>
                                  </span>
                                </Tooltip>
                              )}
                              {canIgnore && (
                                <Tooltip title="Not a real account line: drop it from the queue. Any AI guess on it is discarded; it stays visible and can be restored, and a line in review is already off the sheet, so no total changes.">
                                  <span>
                                    <button
                                      type="button"
                                      className={`${classes.actBtn} ${classes.actIgnore}`}
                                      disabled={locked}
                                      onClick={() => ignoreLine(l._i)}
                                    >
                                      <MdClose /> Ignore
                                    </button>
                                  </span>
                                </Tooltip>
                              )}
                              {l.ignored && (
                                <Tooltip title="Put this line back in the review queue">
                                  <span>
                                    <button
                                      type="button"
                                      className={`${classes.actBtn} ${classes.actIgnore}`}
                                      disabled={locked}
                                      onClick={() => ignoreLine(l._i, true)}
                                    >
                                      <MdUndo /> Restore
                                    </button>
                                  </span>
                                </Tooltip>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {visibleLines.length === 0 && (
                  <NoData
                    text={
                      reviewOnly
                        ? 'Nothing needs review on this document 🎉'
                        : 'No line items'
                    }
                  />
                )}
              </div>
            </>
          )}
        </>
      )}

      {/* Step 5: nothing to edit, just confirmation of what the workbook contains. */}
      {!loading && run && step === 4 && (
        <div className={classes.doneBox}>
          <div className={classes.doneTitle}>Ready to download</div>
          <div className={classes.doneGrid}>
            <div>
              <div className={classes.statLabel}>Documents</div>
              <div className={classes.statValue}>
                {approvedCount} of {docs.length} approved
              </div>
            </div>
            <div>
              <div className={classes.statLabel}>Expense rows</div>
              <div className={classes.statValue}>
                {run?.expenseLayout?.rows?.length || 0}
              </div>
            </div>
            <div>
              <div className={classes.statLabel}>Years covered</div>
              <div className={classes.statValue}>
                {[
                  ...new Set(
                    docs
                      .filter((d) => d.status === 'approved')
                      .map((d) => d.classification?.year)
                      .filter(Boolean),
                  ),
                ]
                  .sort((a, b) => b - a)
                  .join(', ') || 'none'}
              </div>
            </div>
          </div>
          <div className={classes.doneHint}>
            The workbook has seven tabs and live formulas, Excel recalculates on open. Open
            it beside the analyst's copy to compare.
          </div>
        </div>
      )}

      {showRows && run?._id && (
        <ConfirmRowsModal
          show={showRows}
          setShow={setShowRows}
          runId={run._id}
          onConfirmed={() => getRun(run._id)}
        />
      )}
      {showPicker && (
        <SelectFilesModal
          show={showPicker}
          setShow={setShowPicker}
          listingId={listingId}
          onSelect={startRun}
        />
      )}
    </div>
  );
};

export default ExtractionReview;
